import { ColorValue, NumericValue, PropertyTypeValueMap, StyleType } from '@wix/editor-platform-ooi-metadata-resolver';
import {
  createStylesParams,
  type ISettingsColor,
  type ISettingsFont,
  type ISiteTextPreset,
  type IStyleParam,
  type IWixStyleFont,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';
import type { CustomCssVarsFn } from '@wix/yoshi-flow-editor';

export const fontValueFix = (
  fontFn: Function,
  textPresets: ISiteTextPreset,
): IWixStyleFont => {
  const { value: _, ...font } = fontFn({ textPresets });

  return {
    ...font,
  } as IWixStyleFont;
};

type BooleanStyleParam = Omit<
  IStyleParam<StyleParamType.Boolean, boolean>,
  'name'
>;

const booleanParam = (defaultValue: boolean): BooleanStyleParam =>
  ({
    type: StyleParamType.Boolean,
    getDefaultValue: () => defaultValue,
    metadata: {
      type: StyleType.UNKNOWN_CssPropertyType,
      defaultValue: {
        type: 'static',
        value: defaultValue,
      },
    },
  });

type NumberStyleParam = Omit<
  IStyleParam<StyleParamType.Number, number>,
  'name'
>;

type StyleTypeFor<ValueType> = keyof {
  [K in keyof PropertyTypeValueMap as Required<PropertyTypeValueMap[K]> extends Required<ValueType> ? K : never]: K
};

const numberParam = (
  type: StyleTypeFor<NumericValue>,
  defaultValue: number | undefined,
): NumberStyleParam => ({
  type: StyleParamType.Number,
  getDefaultValue: () => defaultValue as number,
  metadata: {
    type: type as StyleType.UNKNOWN_CssPropertyType, // type assertion is needed to avoid strange TypeScript error
    defaultValue: {
      type: 'static',
      value: defaultValue as number,
    },
  },
});

const numberParamWithoutInheritDesktop = (
  type: StyleTypeFor<NumericValue>,
  defaultValue: number,
): NumberStyleParam => ({
  inheritDesktop: false,
  ...numberParam(type, defaultValue),
});

const numberParamWithMobileDefault = (
  type: StyleTypeFor<number>,
  mobileDefaultValue: number,
  defaultValue: number,
): NumberStyleParam => ({
  type: StyleParamType.Number,
  getDefaultValue: ({ isMobile }) =>
    isMobile ? mobileDefaultValue : defaultValue,
  inheritDesktop: false,
  metadata: {
    type: type as StyleType.UNKNOWN_CssPropertyType, // type assertion is needed to avoid strange TypeScript error, because the enum union is too long
    defaultValue: {
      type: 'static',
      value: defaultValue,
    },
    mobileDefaultValue: {
      type: 'static',
      value: mobileDefaultValue,
    },
  },
});

type ColorStyleParam = Omit<
  IStyleParam<StyleParamType.Color, ISettingsColor>,
  'name'
>;

const colorParam = (
  type: StyleTypeFor<ColorValue>,
  preset: string,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(preset),
  metadata: {
    type,
    defaultValue: {
      type: 'static',
      preset,
    },
  },
});

const colorParamWithOpacity = (
  type: StyleTypeFor<ColorValue>,
  preset: string,
  opacity: number,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(preset, opacity),
  metadata: {
    type,
    defaultValue: {
      type: 'static',
      preset,
      opacity,
    },
  },
});

type FontStyleParam = Omit<
  IStyleParam<StyleParamType.Font, ISettingsFont>,
  'name'
>;

const fontParam = (
  preset: keyof ISiteTextPreset,
  props: { size?: number; htmlTag?: string },
): FontStyleParam => ({
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam(preset, props),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset,
      ...props,
    },
  },
});

const fontParamWithMobileDefault = (
  preset: keyof ISiteTextPreset,
  mobileSize: number,
  size: number,
): FontStyleParam => ({
  type: StyleParamType.Font,
  getDefaultValue: ({ textPresets, isMobile }) => fontValueFix(
    wixFontParam(preset, {
      size: isMobile ? mobileSize : size,
    }),
    textPresets,
  ),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset,
      size,
    },
    mobileDefaultValue: {
      type: 'static',
      preset,
      size: mobileSize,
    },
  },
});

const stylesParams = createStylesParams<{
  sectionTitleBookButtonVisibility: StyleParamType.Boolean;
  sectionSchedulingBookButtonVisibility: StyleParamType.Boolean;
  sectionContactBookButtonVisibility: StyleParamType.Boolean;
  sectionDescriptionBookButtonVisibility: StyleParamType.Boolean;
  sectionDetailsBookButtonVisibility: StyleParamType.Boolean;
  sectionGalleryBookButtonVisibility: StyleParamType.Boolean;
  sectionPluginBookButtonVisibility: StyleParamType.Boolean;
  sectionPolicyBookButtonVisibility: StyleParamType.Boolean;
  sectionTitleVisibility: StyleParamType.Boolean;
  sectionSchedulingVisibility: StyleParamType.Boolean;
  sectionContactVisibility: StyleParamType.Boolean;
  sectionDescriptionVisibility: StyleParamType.Boolean;
  sectionDetailsVisibility: StyleParamType.Boolean;
  sectionGalleryVisibility: StyleParamType.Boolean;
  sectionPluginVisibility: StyleParamType.Boolean;
  sectionPolicyVisibility: StyleParamType.Boolean;
  sectionTitleDivider: StyleParamType.Boolean;
  sectionSchedulingDivider: StyleParamType.Boolean;
  sectionContactDivider: StyleParamType.Boolean;
  sectionDescriptionDivider: StyleParamType.Boolean;
  sectionDetailsDivider: StyleParamType.Boolean;
  sectionGalleryDivider: StyleParamType.Boolean;
  sectionPluginDivider: StyleParamType.Boolean;
  sectionPolicyDivider: StyleParamType.Boolean;
  headerVisibility: StyleParamType.Boolean;
  headerTitleVisibility: StyleParamType.Boolean;
  headerImageVisibility: StyleParamType.Boolean;
  headerBookButtonVisibility: StyleParamType.Boolean;
  sidebarVisibility: StyleParamType.Boolean;
  onlineBadgeVisibility: StyleParamType.Boolean;
  serviceTitleVisibility: StyleParamType.Boolean;
  serviceTaglineVisibility: StyleParamType.Boolean;
  detailsDurationVisibility: StyleParamType.Boolean;
  detailsPriceVisibility: StyleParamType.Boolean;
  detailsLocationVisibility: StyleParamType.Boolean;
  businessEmailVisibility: StyleParamType.Boolean;
  businessPhoneNumberVisibility: StyleParamType.Boolean;
  businessAddressVisibility: StyleParamType.Boolean;
  locationNameVisibility: StyleParamType.Boolean;
  scheduleDurationVisibility: StyleParamType.Boolean;
  scheduleStaffVisibility: StyleParamType.Boolean;
  headerHeight: StyleParamType.Number;
  headerSidePadding: StyleParamType.Number;
  headerImageOpacity: StyleParamType.Number;
  headerImageBackgroundColor: StyleParamType.Color;
  headerBackgroundColor: StyleParamType.Color;
  headerTitleFont: StyleParamType.Font;
  headerTitleColor: StyleParamType.Color;
  bodyBackgroundColor: StyleParamType.Color;
  bodyServiceTitleFont: StyleParamType.Font;
  bodyServiceTitleColor: StyleParamType.Color;
  bodyServiceTaglineFont: StyleParamType.Font;
  bodyServiceTaglineColor: StyleParamType.Color;
  bodySectionTitleFont: StyleParamType.Font;
  bodySectionTitleColor: StyleParamType.Color;
  bodyTextFont: StyleParamType.Font;
  bodyTextColor: StyleParamType.Color;
  courseAvailabilityTextFont: StyleParamType.Font;
  courseAvailabilityTextColor: StyleParamType.Color;
  bodyDividerWidth: StyleParamType.Number;
  bodyDividerColor: StyleParamType.Color;
  sidebarBackgroundColor: StyleParamType.Color;
  sidebarTitleColor: StyleParamType.Color;
  sidebarTitleFont: StyleParamType.Font;
  sidebarTextColor: StyleParamType.Color;
  sidebarTextFont: StyleParamType.Font;
  sidebarServiceTitleColor: StyleParamType.Color;
  sidebarServiceTitleFont: StyleParamType.Font;
  sidebarServiceTaglineColor: StyleParamType.Color;
  sidebarServiceTaglineFont: StyleParamType.Font;
  sidebarButtonTextColor: StyleParamType.Color;
  sidebarButtonTextFont: StyleParamType.Font;
  sidebarButtonBackgroundColor: StyleParamType.Color;
  sidebarButtonBorderColor: StyleParamType.Color;
  sidebarButtonBorderWidth: StyleParamType.Number;
  sidebarButtonCornerRadius: StyleParamType.Number;
  sidebarButtonHoverTextColor: StyleParamType.Color;
  sidebarButtonHoverBackgroundColor: StyleParamType.Color;
  sidebarButtonHoverBorderColor: StyleParamType.Color;
  sidebarWidth: StyleParamType.Number;
  sidebarSidePadding: StyleParamType.Number;
  sidebarSideSpacing: StyleParamType.Number;
  sidebarSpaceFromTheTop: StyleParamType.Number;
  headerButtonTextColor: StyleParamType.Color;
  headerButtonTextFont: StyleParamType.Font;
  headerButtonBackgroundColor: StyleParamType.Color;
  headerButtonBorderColor: StyleParamType.Color;
  headerButtonBorderWidth: StyleParamType.Number;
  headerButtonCornerRadius: StyleParamType.Number;
  headerButtonHoverTextColor: StyleParamType.Color;
  headerButtonHoverBackgroundColor: StyleParamType.Color;
  headerButtonHoverBorderColor: StyleParamType.Color;
  bodyButtonTextColor: StyleParamType.Color;
  bodyButtonTextFont: StyleParamType.Font;
  bodyButtonBackgroundColor: StyleParamType.Color;
  bodyButtonBorderColor: StyleParamType.Color;
  bodyButtonBorderWidth: StyleParamType.Number;
  bodyButtonCornerRadius: StyleParamType.Number;
  bodyButtonHoverTextColor: StyleParamType.Color;
  bodyButtonHoverBackgroundColor: StyleParamType.Color;
  bodyButtonHoverBorderColor: StyleParamType.Color;
  bodySidePadding: StyleParamType.Number;
  bodySpaceBetweenSections: StyleParamType.Number;
  firstSectionSpaceFromTheTop: StyleParamType.Number;
  backgroundColor: StyleParamType.Color;
  detailsInnerPadding: StyleParamType.Number;
  detailsBoxFont: StyleParamType.Font;
  detailsBoxTextColor: StyleParamType.Color;
  detailsBoxColor: StyleParamType.Color;
  detailsBoxBorderColor: StyleParamType.Color;
  detailsBoxBorderWidth: StyleParamType.Number;
  detailsBoxBorderRadius: StyleParamType.Number;
  scheduleBoxPadding: StyleParamType.Number;
  scheduleBackgroundColor: StyleParamType.Color;
  scheduleBorderColor: StyleParamType.Color;
  scheduleBorderWidth: StyleParamType.Number;
  scheduleBorderRadius: StyleParamType.Number;
  scheduleDividerColor: StyleParamType.Color;
  scheduleDividerWidth: StyleParamType.Number;
  scheduleDatesFont: StyleParamType.Font;
  scheduleDatesColor: StyleParamType.Color;
  scheduleHoursFont: StyleParamType.Font;
  scheduleHoursColor: StyleParamType.Color;
  scheduleDetailsFont: StyleParamType.Font;
  scheduleDetailsColor: StyleParamType.Color;
  scheduleLinkFont: StyleParamType.Font;
  scheduleLinkColor: StyleParamType.Color;
  galleryMasonryItemBorderColor: StyleParamType.Color;
  galleryGridItemBorderColor: StyleParamType.Color;
  gallerySliderItemBorderColor: StyleParamType.Color;
  gallerySliderArrowColor: StyleParamType.Color;
  mobileServiceTitleFontSize: StyleParamType.Number;
  mobileServiceTaglineFontSize: StyleParamType.Number;
  mobileSectionTitleFontSize: StyleParamType.Number;
  mobileTextFontSize: StyleParamType.Number;
  mobileButtonTextSize: StyleParamType.Number;
  mobileHeaderTitleFontSize: StyleParamType.Number;
  mobileHeaderButtonTextSize: StyleParamType.Number;
  mobileDetailsBoxFontSize: StyleParamType.Number;
  mobileScheduleDatesFontSize: StyleParamType.Number;
  mobileScheduleHoursFontSize: StyleParamType.Number;
  mobileScheduleDetailsFontSize: StyleParamType.Number;
  mobileScheduleLinkFontSize: StyleParamType.Number;
}>({
  sectionTitleBookButtonVisibility: booleanParam(false),
  sectionSchedulingBookButtonVisibility: booleanParam(true),
  sectionContactBookButtonVisibility: booleanParam(false),
  sectionDescriptionBookButtonVisibility: booleanParam(false),
  sectionDetailsBookButtonVisibility: booleanParam(true),
  sectionGalleryBookButtonVisibility: booleanParam(false),
  sectionPluginBookButtonVisibility: booleanParam(false),
  sectionPolicyBookButtonVisibility: booleanParam(false),
  sectionTitleVisibility: booleanParam(true),
  sectionSchedulingVisibility: booleanParam(true),
  sectionContactVisibility: booleanParam(true),
  sectionDescriptionVisibility: booleanParam(true),
  sectionDetailsVisibility: booleanParam(true),
  sectionGalleryVisibility: booleanParam(true),
  sectionPluginVisibility: booleanParam(true),
  sectionPolicyVisibility: booleanParam(true),
  sectionTitleDivider: booleanParam(true),
  sectionSchedulingDivider: booleanParam(true),
  sectionContactDivider: booleanParam(true),
  sectionDescriptionDivider: booleanParam(true),
  sectionDetailsDivider: booleanParam(true),
  sectionGalleryDivider: booleanParam(true),
  sectionPluginDivider: booleanParam(true),
  sectionPolicyDivider: booleanParam(true),
  headerVisibility: booleanParam(true),
  headerTitleVisibility: booleanParam(false),
  headerImageVisibility: booleanParam(true),
  headerBookButtonVisibility: booleanParam(false),
  sidebarVisibility: booleanParam(false),
  onlineBadgeVisibility: booleanParam(true),
  serviceTitleVisibility: booleanParam(true),
  serviceTaglineVisibility: booleanParam(true),
  detailsDurationVisibility: booleanParam(true),
  detailsPriceVisibility: booleanParam(true),
  detailsLocationVisibility: booleanParam(true),
  businessEmailVisibility: booleanParam(true),
  businessPhoneNumberVisibility: booleanParam(true),
  businessAddressVisibility: booleanParam(true),
  locationNameVisibility: booleanParam(false),
  scheduleDurationVisibility: booleanParam(true),
  scheduleStaffVisibility: booleanParam(true),
  headerHeight: numberParamWithMobileDefault(StyleType.height, 220, 360),
  headerSidePadding: numberParamWithoutInheritDesktop(StyleType.padding, 20),
  headerImageOpacity: numberParam(StyleType.opacity as any, 100),
  headerImageBackgroundColor: colorParam(StyleType.backgroundColor, 'color-5'),
  bodyBackgroundColor: colorParam(StyleType.backgroundColor, 'color-1'),
  headerBackgroundColor: colorParam(StyleType.backgroundColor, 'color-5'),  
  headerTitleFont: fontParamWithMobileDefault('Page-title', 32, 40),
  headerTitleColor: colorParam(StyleType.color, 'color-1'),
  bodySectionTitleFont: fontParam('Page-title', { size: 20 }),
  bodySectionTitleColor: colorParam(StyleType.color, 'color-5'),
  bodyTextFont: fontParamWithMobileDefault('Body-M', 14, 16),
  bodyTextColor: colorParam(StyleType.color, 'color-5'),
  courseAvailabilityTextFont: fontParamWithMobileDefault('Body-M', 14, 16),
  courseAvailabilityTextColor: colorParam(StyleType.color, 'color-5'),
  bodySidePadding: numberParamWithoutInheritDesktop(StyleType.padding, 20),
  firstSectionSpaceFromTheTop: numberParamWithoutInheritDesktop(StyleType.marginTop, 48),
  bodySpaceBetweenSections: numberParamWithoutInheritDesktop(StyleType.gap, 32),
  sidebarBackgroundColor: colorParam(StyleType.backgroundColor, 'color-1'),
  sidebarTitleColor: colorParam(StyleType.color, 'color-5'),
  sidebarTitleFont: fontParam('Page-title', { size: 20 }),
  sidebarTextColor: colorParam(StyleType.color, 'color-5'),
  sidebarTextFont: fontParamWithMobileDefault('Body-M', 14, 16),
  sidebarServiceTitleColor: colorParam(StyleType.color, 'color-5'),
  sidebarServiceTitleFont: fontParamWithMobileDefault('Page-title', 32, 40),
  sidebarServiceTaglineColor: colorParam(StyleType.color, 'color-5'),
  sidebarServiceTaglineFont: fontParamWithMobileDefault('Body-M', 14, 16),
  sidebarWidth: numberParam(StyleType.width, 30),
  sidebarSidePadding: numberParamWithoutInheritDesktop(StyleType.padding, 20),
  sidebarSideSpacing: numberParamWithoutInheritDesktop(StyleType.gap, 0),
  sidebarSpaceFromTheTop: numberParamWithoutInheritDesktop(StyleType.marginTop, 48),
  backgroundColor: colorParam(StyleType.backgroundColor, 'color-5'),
  detailsInnerPadding: numberParamWithoutInheritDesktop(StyleType.padding, 16),
  detailsBoxFont: fontParamWithMobileDefault('Body-M', 14, 16),
  detailsBoxTextColor: colorParam(StyleType.color, 'color-5'),
  detailsBoxColor: colorParamWithOpacity(StyleType.backgroundColor, 'color-1', 0),
  detailsBoxBorderColor: colorParamWithOpacity(StyleType.borderColor, 'color-5', 0.2),
  detailsBoxBorderWidth: numberParam(StyleType.borderWidth, 1),
  sidebarButtonTextColor: colorParam(StyleType.color, 'color-1'),
  sidebarButtonTextFont: fontParamWithMobileDefault('Body-M', 14, 16),
  sidebarButtonBackgroundColor: colorParam(StyleType.backgroundColor, 'color-8'),
  sidebarButtonBorderColor: colorParam(StyleType.borderColor, 'color-8'),
  sidebarButtonBorderWidth: numberParam(StyleType.borderWidth, 0),
  sidebarButtonCornerRadius: numberParam(StyleType.borderRadius, 1),
  sidebarButtonHoverTextColor: colorParam(StyleType.color, 'color-1'),
  sidebarButtonHoverBackgroundColor: colorParam(StyleType.backgroundColor, 'color-8'),
  sidebarButtonHoverBorderColor: colorParam(StyleType.borderColor, 'color-8'),
  bodyButtonTextColor: colorParam(StyleType.color, 'color-1'),
  bodyButtonTextFont: fontParamWithMobileDefault('Body-M', 14, 16),
  bodyButtonBackgroundColor: colorParamWithOpacity(StyleType.backgroundColor, 'color-8', 1),
  bodyButtonBorderColor: colorParamWithOpacity(StyleType.borderColor, 'color-8', 1),
  bodyButtonBorderWidth: numberParam(StyleType.borderWidth, 0),
  bodyButtonCornerRadius: numberParam(StyleType.borderRadius, 0),
  bodyButtonHoverTextColor: colorParam(StyleType.color, 'color-1'),
  bodyButtonHoverBackgroundColor: colorParam(StyleType.backgroundColor, 'color-8'),
  bodyButtonHoverBorderColor: colorParam(StyleType.borderColor, 'color-8'),
  headerButtonTextColor: colorParam(StyleType.color, 'color-5'),
  headerButtonTextFont: fontParamWithMobileDefault('Body-M', 14, 16),
  headerButtonBackgroundColor: colorParam(StyleType.backgroundColor, 'color-1'),
  headerButtonBorderColor: colorParam(StyleType.borderColor, 'color-1'),
  headerButtonBorderWidth: numberParam(StyleType.borderWidth, 0),
  headerButtonCornerRadius: numberParam(StyleType.borderRadius, 0),
  headerButtonHoverTextColor: colorParam(StyleType.color, 'color-5'),
  headerButtonHoverBackgroundColor: colorParam(StyleType.backgroundColor, 'color-1'),
  headerButtonHoverBorderColor: colorParam(StyleType.borderColor, 'color-1'),
  bodyDividerWidth: numberParam(StyleType.borderWidth, 1),
  detailsBoxBorderRadius: numberParam(StyleType.borderRadius, 0),
  bodyDividerColor: colorParamWithOpacity(StyleType.borderColor, 'color-5', 0.2),
  bodyServiceTitleFont: fontParamWithMobileDefault('Page-title', 32, 40),
  bodyServiceTitleColor: colorParam(StyleType.color, 'color-5'),
  bodyServiceTaglineFont: fontParamWithMobileDefault('Body-M', 14, 16),
  bodyServiceTaglineColor: colorParam(StyleType.color, 'color-5'),
  scheduleBoxPadding: numberParamWithMobileDefault(StyleType.padding, 20, 28),
  scheduleBackgroundColor: colorParamWithOpacity(StyleType.backgroundColor, 'color-1', 0),
  scheduleBorderColor: colorParamWithOpacity(StyleType.borderColor, 'color-5', 0.2),
  scheduleBorderWidth: numberParam(StyleType.borderWidth, 1),
  scheduleBorderRadius: numberParam(StyleType.borderRadius, 0),
  scheduleDividerColor: colorParamWithOpacity(StyleType.borderColor, 'color-5', 0.2),
  scheduleDividerWidth: numberParam(StyleType.borderWidth, 1),
  scheduleDatesFont: fontParamWithMobileDefault('Body-M', 14, 16),
  scheduleDatesColor: colorParam(StyleType.color, 'color-5'),
  scheduleHoursFont: fontParamWithMobileDefault('Body-M', 14, 16),
  scheduleHoursColor: colorParam(StyleType.color, 'color-5'),
  scheduleDetailsFont: fontParam('Body-M', { size: 14 }),
  scheduleDetailsColor: colorParam(StyleType.color, 'color-4'),
  scheduleLinkFont: fontParamWithMobileDefault('Body-M', 14, 16),
  scheduleLinkColor: colorParam(StyleType.color, 'color-8'),
  galleryMasonryItemBorderColor: colorParamWithOpacity(StyleType.borderColor, 'color-5', 0.2),
  galleryGridItemBorderColor: colorParamWithOpacity(StyleType.borderColor, 'color-5', 0.2),
  gallerySliderItemBorderColor: colorParamWithOpacity(StyleType.borderColor, 'color-5', 0.2),
  gallerySliderArrowColor: colorParam(StyleType.color, 'color-5'),
  mobileServiceTitleFontSize: numberParam(StyleType.fontSize, 32),
  mobileServiceTaglineFontSize: numberParam(StyleType.fontSize, 14),
  mobileSectionTitleFontSize: numberParam(StyleType.fontSize, 20),
  mobileTextFontSize: numberParam(StyleType.fontSize, 14),
  mobileButtonTextSize: numberParam(StyleType.fontSize, 14),
  mobileHeaderTitleFontSize: numberParam(StyleType.fontSize, 32),
  mobileHeaderButtonTextSize: numberParam(StyleType.fontSize, 14),
  mobileDetailsBoxFontSize: numberParam(StyleType.fontSize, 14),
  mobileScheduleDatesFontSize: numberParam(StyleType.fontSize, 14),
  mobileScheduleHoursFontSize: numberParam(StyleType.fontSize, 14),
  mobileScheduleDetailsFontSize: numberParam(StyleType.fontSize, 14),
  mobileScheduleLinkFontSize: numberParam(StyleType.fontSize, 14),
});

export default stylesParams;

// noinspection JSUnusedGlobalSymbols
export const customCssVars: CustomCssVarsFn = ({ styleParams }) => {
  const displayStyle = (
    booleanStyleParam: keyof typeof stylesParams,
    visibleDisplay = 'block',
  ) => (styleParams.booleans[booleanStyleParam] ? visibleDisplay : 'none');

  const calculateMargin = (booleanStyleParam: keyof typeof stylesParams) =>
    styleParams.booleans[booleanStyleParam]
      ? styleParams.numbers.bodySpaceBetweenSections
      : 0;

  return {
    headerDisplay: displayStyle('headerVisibility', 'flex'),
    headerTitleDisplay: displayStyle('headerTitleVisibility'),
    headerImageDisplay: displayStyle('headerImageVisibility'),
    headerBookButtonDisplay: displayStyle('headerBookButtonVisibility'),
    sidebarDisplay: displayStyle('sidebarVisibility'),
    onlineBadgeDisplay: displayStyle('onlineBadgeVisibility'),
    serviceTitleDisplay: displayStyle('serviceTitleVisibility'),
    serviceTaglineDisplay: displayStyle('serviceTaglineVisibility'),
    detailsDurationDisplay: displayStyle('detailsDurationVisibility'),
    detailsPriceDisplay: displayStyle('detailsPriceVisibility'),
    detailsLocationDisplay: displayStyle('detailsLocationVisibility'),
    businessEmailDisplay: displayStyle('businessEmailVisibility'),
    businessPhoneNumberDisplay: displayStyle('businessPhoneNumberVisibility'),
    businessAddressDisplay: displayStyle('businessAddressVisibility'),
    locationNameDisplay: displayStyle('locationNameVisibility'),
    scheduleDurationDisplay: displayStyle('scheduleDurationVisibility'),
    scheduleStaffDisplay: displayStyle('scheduleStaffVisibility'),
    sectionTitleBookButtonDisplay: displayStyle(
      'sectionTitleBookButtonVisibility',
    ),
    sectionSchedulingBookButtonDisplay: displayStyle(
      'sectionSchedulingBookButtonVisibility',
    ),
    sectionContactBookButtonDisplay: displayStyle(
      'sectionContactBookButtonVisibility',
    ),
    sectionDescriptionBookButtonDisplay: displayStyle(
      'sectionDescriptionBookButtonVisibility',
    ),
    sectionDetailsBookButtonDisplay: displayStyle(
      'sectionDetailsBookButtonVisibility',
    ),
    sectionGalleryBookButtonDisplay: displayStyle(
      'sectionGalleryBookButtonVisibility',
    ),
    sectionPluginBookButtonDisplay: displayStyle(
      'sectionPluginBookButtonVisibility',
    ),
    sectionPolicyBookButtonDisplay: displayStyle(
      'sectionPolicyBookButtonVisibility',
    ),
    sectionTitleDisplay: displayStyle('sectionTitleVisibility'),
    sectionSchedulingDisplay: displayStyle('sectionSchedulingVisibility'),
    sectionContactDisplay: displayStyle('sectionContactVisibility'),
    sectionDescriptionDisplay: displayStyle('sectionDescriptionVisibility'),
    sectionDetailsDisplay: displayStyle('sectionDetailsVisibility'),
    sectionGalleryDisplay: displayStyle('sectionGalleryVisibility'),
    sectionPluginDisplay: displayStyle('sectionPluginVisibility'),
    sectionPolicyDisplay: displayStyle('sectionPolicyVisibility'),
    SectionTitleDividerDisplay: displayStyle('sectionTitleDivider'),
    sectionSchedulingDividerDisplay: displayStyle('sectionSchedulingDivider'),
    sectionContactDividerDisplay: displayStyle('sectionContactDivider'),
    sectionDescriptionDividerDisplay: displayStyle('sectionDescriptionDivider'),
    sectionDetailsDividerDisplay: displayStyle('sectionDetailsDivider'),
    sectionGalleryDividerDisplay: displayStyle('sectionGalleryDivider'),
    sectionPluginDividerDisplay: displayStyle('sectionPluginDivider'),
    sectionPolicyDividerDisplay: displayStyle('sectionPolicyDivider'),
    sectionTitleMargin: calculateMargin('sectionTitleDivider'),
    sectionSchedulingMargin: calculateMargin('sectionSchedulingDivider'),
    sectionContactMargin: calculateMargin('sectionContactDivider'),
    sectionDescriptionMargin: calculateMargin('sectionDescriptionDivider'),
    sectionDetailsMargin: calculateMargin('sectionDetailsDivider'),
    sectionGalleryMargin: calculateMargin('sectionGalleryDivider'),
    sectionPluginMargin: calculateMargin('sectionPluginDivider'),
    sectionPolicyMargin: calculateMargin('sectionPolicyDivider'),
  };
};
